import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Button } from '@praetorian-chariot/ui';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import {
  useGenerateRecoveryCodes,
  useGetRecoveryCodesInfo,
} from '@/hooks/useRecoveryCodes';

interface ViewRecoveryCodesModalProps {
  open: boolean;
  onClose: () => void;
}

export const ViewRecoveryCodesModal: React.FC<ViewRecoveryCodesModalProps> = ({
  open,
  onClose,
}) => {
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [hasExistingCodes, setHasExistingCodes] = useState(false);
  const [copied, setCopied] = useState(false);

  const { getInfo, isLoading: isLoadingInfo } = useGetRecoveryCodesInfo();
  const { mutateAsync: generateCodes, status: generateStatus } =
    useGenerateRecoveryCodes();

  useEffect(() => {
    if (open) {
      checkExistingCodes();
    }
  }, [open]);

  const checkExistingCodes = async () => {
    try {
      const info = await getInfo();
      if (info && info?.total_codes && info.total_codes > 0) {
        setHasExistingCodes(true);
      } else {
        setHasExistingCodes(false);
      }
    } catch (error) {
      setHasExistingCodes(false);
    }
  };

  const handleGenerateCodes = async () => {
    try {
      const response = await generateCodes({ count: 8 });
      if (response.codes) {
        setRecoveryCodes(response.codes);
        setHasExistingCodes(true);
      }
    } catch (error) {
      console.error('Failed to generate recovery codes:', error);
    }
  };

  const handleCopyToClipboard = () => {
    const codesText = recoveryCodes.join('\n');
    navigator.clipboard
      .writeText(codesText)
      .then(() => {
        setCopied(true);
        toast.success('Recovery codes copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.error('Failed to copy to clipboard');
      });
  };

  const handleClose = () => {
    setRecoveryCodes([]);
    setCopied(false);
    onClose();
  };

  const isLoading = isLoadingInfo || generateStatus === 'pending';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Recovery Codes"
      size="xl"
      hideFooter
    >
      <div className="mb-4 flex flex-col gap-6">
        {/* Header Description */}
        <div className="flex flex-col gap-2">
          <p className="text-chariot-text-secondary text-sm">
            Recovery codes are one-time use codes that allow you to access your
            account if you lose access to your authenticator app. Keep them safe
            and secure.
          </p>
          <div className="bg-chariot-background-secondary flex items-center gap-2 rounded-md p-3">
            <div className="text-chariot-brand-primary">⚠️</div>
            <p className="text-chariot-text-secondary text-xs">
              <strong>Important:</strong> Store these codes securely. Each code
              can only be used once.
            </p>
          </div>
        </div>

        <Loader isLoading={isLoading} className="h-48">
          {/* Recovery Codes Display */}
          {recoveryCodes.length > 0 ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h3 className="text-chariot-text-primary text-lg font-semibold">
                  Your Recovery Codes
                </h3>
                <Button
                  onClick={handleCopyToClipboard}
                  startIcon={
                    copied ? (
                      <CheckIcon className="size-4 text-green-500" />
                    ) : (
                      <ClipboardDocumentIcon className="size-4" />
                    )
                  }
                  label={copied ? 'Copied!' : 'Copy All'}
                  styleType="secondary"
                  size="sm"
                />
              </div>

              {/* Codes Grid */}
              <div className="grid grid-cols-2 gap-3">
                {recoveryCodes.map((code, index) => (
                  <div
                    key={index}
                    className="border-chariot-stroke bg-chariot-background-primary flex items-center justify-center rounded-md border p-3"
                  >
                    <span className="text-chariot-text-primary font-mono text-sm">
                      {code}
                    </span>
                  </div>
                ))}
              </div>

              {/* Instructions */}
              <div className="border-chariot-stroke bg-chariot-background-secondary rounded-md border p-4">
                <h4 className="text-chariot-text-primary mb-2 text-sm font-semibold">
                  How to use recovery codes:
                </h4>
                <ul className="text-chariot-text-secondary space-y-1 text-xs">
                  <li>
                    • Save these codes in a secure location (password manager,
                    safe, etc.)
                  </li>
                  <li>
                    • Use a recovery code when you can&apos;t access your
                    authenticator app
                  </li>
                  <li>• Each code can only be used once</li>
                  <li>• Generate new codes if you&apos;ve used most of them</li>
                </ul>
              </div>
            </div>
          ) : (
            /* Generate New Codes */
            <div className="flex flex-col items-center gap-4 py-8">
              {hasExistingCodes ? (
                <div className="mx-auto text-center">
                  <h3 className="text-chariot-text-primary mb-2 text-lg font-semibold">
                    Recovery Codes Available
                  </h3>
                  <p className="text-chariot-text-secondary mb-4 text-sm">
                    You have existing recovery codes. Generate new codes to view
                    and replace them.
                  </p>
                  <Button
                    onClick={handleGenerateCodes}
                    label="Generate New Codes"
                    styleType="primary"
                    className="mx-auto"
                    disabled={isLoading}
                  />
                </div>
              ) : (
                <div className="text-center">
                  <h3 className="text-chariot-text-primary mb-2 text-lg font-semibold">
                    No Recovery Codes
                  </h3>
                  <p className="text-chariot-text-secondary mb-4 text-sm">
                    Generate recovery codes to use as backup authentication when
                    you can&apos;t access your authenticator app.
                  </p>
                  <Button
                    className="mx-auto"
                    onClick={handleGenerateCodes}
                    label="Generate Recovery Codes"
                    styleType="primary"
                    disabled={isLoading}
                  />
                </div>
              )}
            </div>
          )}
        </Loader>
      </div>
    </Modal>
  );
};
